import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Toast, useToast } from "@hiya/ui";
import { getConsultation, saveNotes, publishConsultation } from "../../../api/consultations";
import { ConsultationPreviewModal } from "./ConsultationPreviewModal";
import { formatDateTime } from "../../../utils/formatDate";
import type { ConsultationDetail } from "../../../types/consultation";
import { DetailLoadingState, DetailNotFoundState } from "../../../components/DetailPageStates";
import { InfoCard } from "../../../components/InfoCard";
import { ContentSection } from "../../../components/ContentSection";
import { ConfirmPublishModal } from "../../../components/ConfirmPublishModal";
import { PublishedTimestamp } from "../../../components/PublishedTimestamp";
import { UserIcon, NoteIcon, PlanIcon, DocumentIcon, EyeIcon } from "../../../components/Icons";
import { useAuthUser } from "../../../auth/AuthContext";

const TEXTAREA_CLASSES =
  "w-full rounded-2xl border border-stroke-primary bg-bg-primary p-4 text-sm leading-[1.5] text-text-primary placeholder:text-text-tertiary outline-none focus:border-brand-brown resize-none disabled:bg-bg-secondary disabled:text-text-secondary";

export function ConsultationWorkspacePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast, showToast } = useToast();
  const authUser = useAuthUser();

  const [consultation, setConsultation] = useState<ConsultationDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [showPreview, setShowPreview] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);

  // Form state
  const [doctorNote, setDoctorNote] = useState("");
  const [planNextSteps, setPlanNextSteps] = useState("");
  const [recommendations, setRecommendations] = useState("");
  const [attestation, setAttestation] = useState(false);

  const isPublished = consultation?.status === "notes_published";
  // HH-300 AC-f: a cancelled consultation must not be editable or publishable
  // from the UI. Backend rejects save/publish with 409, but surfacing the
  // state here gives the doctor a clear signal as soon as they navigate
  // here (e.g. via a "Consultation cancelled" notification deep link).
  const isCanceled = consultation?.status === "canceled";
  const isReadOnly = isPublished || isCanceled;
  const allFieldsFilled = doctorNote.trim() !== "" && planNextSteps.trim() !== "" && recommendations.trim() !== "";
  const canPublish = allFieldsFilled && attestation && !isReadOnly;

  const loadConsultation = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const data = await getConsultation(id);
      setConsultation(data);
      setDoctorNote(data.doctor_note ?? "");
      setPlanNextSteps(data.plan_next_steps ?? "");
      setRecommendations(data.recommendations ?? "");
      setAttestation(data.attestation_checked);
    } catch {
      showToast("Something went wrong. Please refresh the page.", "error");
    } finally {
      setLoading(false);
    }
  }, [id, showToast]);

  useEffect(() => {
    loadConsultation();
  }, [loadConsultation]);

  async function handlePublish() {
    if (!id || !canPublish) return;
    try {
      setPublishing(true);
      // Save notes first to ensure latest content
      await saveNotes(id, {
        doctor_note: doctorNote,
        plan_next_steps: planNextSteps,
        recommendations,
      });
      await publishConsultation(id, attestation);
      setShowPublishModal(false);
      showToast(`Consultation notes for Case ID ${consultation?.case_id ?? id} have been published`);
      navigate("/dashboard/consultations");
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Failed to publish", "error");
      setShowPublishModal(false);
    } finally {
      setPublishing(false);
    }
  }

  if (loading) return <DetailLoadingState />;
  if (!consultation) return <DetailNotFoundState message="Consultation not found" />;

  return (
    <div>
      <Toast message={toast.message} visible={toast.visible} variant={toast.variant} />

      {/* HH-300 AC-f: surface cancellation when the doctor lands on the
          workspace for a cancelled consultation (e.g. via notification
          deep link). Editing and publishing are disabled below. */}
      {isCanceled && (
        <div
          role="status"
          className="mb-6 rounded-2xl border border-stroke-primary bg-bg-tertiary px-4 py-3 text-sm text-text-secondary"
        >
          <p className="font-medium text-text-primary">This consultation was cancelled.</p>
          <p>Editing and publishing are disabled. The patient was notified by the CRM.</p>
        </div>
      )}

      {/* Sections */}
      <div className="flex flex-col gap-10">
        {/* Client Info */}
        <ContentSection icon={<UserIcon />} title="Client Info">
          <InfoCard>
            <div className="flex gap-4">
              <div className="flex-1">
                <span className="text-sm text-text-tertiary">Case ID</span>
                <p className="text-sm text-text-primary">{consultation.case_id ?? "-"}</p>
              </div>
              <div className="flex-1">
                <span className="text-sm text-text-tertiary">Date of consultation</span>
                <p className="text-sm text-text-primary">{formatDateTime(consultation.scheduled_time)}</p>
              </div>
            </div>
          </InfoCard>
        </ContentSection>

        {/* Doctor Note */}
        <ContentSection icon={<NoteIcon />} title="Doctor Note">
          <textarea
            value={doctorNote}
            onChange={(e) => setDoctorNote(e.target.value)}
            placeholder="Your note..."
            disabled={isReadOnly}
            rows={3}
            className={TEXTAREA_CLASSES}
          />
        </ContentSection>

        {/* Plan / Next Steps */}
        <ContentSection icon={<PlanIcon />} title="Plan / next steps">
          <textarea
            value={planNextSteps}
            onChange={(e) => setPlanNextSteps(e.target.value)}
            placeholder="Write plan..."
            disabled={isReadOnly}
            rows={3}
            className={TEXTAREA_CLASSES}
          />
        </ContentSection>

        {/* Recommendations */}
        <ContentSection icon={<DocumentIcon />} title="Recommendations">
          <textarea
            value={recommendations}
            onChange={(e) => setRecommendations(e.target.value)}
            placeholder="Write recommendations..."
            disabled={isReadOnly}
            rows={3}
            className={TEXTAREA_CLASSES}
          />
        </ContentSection>
      </div>

      {/* Footer: attestation + signature + actions */}
      {!isReadOnly && (
        <div className="flex items-center gap-10 mt-6 px-6">
          {/* Left: attestation checkbox */}
          <div className="flex-1">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={attestation}
                onChange={(e) => setAttestation(e.target.checked)}
                className="mt-1 size-4 rounded border-stroke-secondary accent-brand-brown shrink-0"
              />
              <span className="text-sm leading-[1.5] text-text-secondary">
                I confirm that the information is correct and sign the doctor&apos;s document.
              </span>
            </label>
          </div>

          {/* Center: doctor name + signature (greyed until attestation) */}
          <div className={`flex items-center gap-4 ${attestation ? "" : "opacity-30"}`}>
            {(consultation.doctor_name || authUser?.displayName) && (
              <span className="text-sm text-text-quaternary">{consultation.doctor_name || authUser?.displayName}</span>
            )}
            {consultation.signature_url && (
              <img
                src={consultation.signature_url}
                alt="Doctor signature"
                className="h-[80px] w-[120px] object-contain"
              />
            )}
          </div>

          {/* Right: Preview + Publish buttons */}
          <div className="flex gap-2 shrink-0">
            <button
              onClick={() => setShowPreview(true)}
              className="size-[51px] flex items-center justify-center rounded-2xl bg-bg-secondary border border-stroke-primary"
              title="Preview PDF"
            >
              <EyeIcon />
            </button>
            <button
              onClick={() => setShowPublishModal(true)}
              disabled={!canPublish}
              className={`h-[51px] px-6 rounded-2xl text-text-btn text-base font-semibold ${
                canPublish ? "bg-btn-action" : "bg-btn-action-disabled"
              }`}
            >
              Approve and publish to client
            </button>
          </div>
        </div>
      )}

      {isPublished && <PublishedTimestamp publishedAt={consultation.published_at} />}

      <ConfirmPublishModal
        open={showPublishModal}
        title="Publish Consultation"
        message="Are you sure you want to publish? Once published, the consultation notes will be visible to the patient and cannot be edited."
        publishing={publishing}
        onClose={() => setShowPublishModal(false)}
        onConfirm={handlePublish}
      />

      <ConsultationPreviewModal
        open={showPreview}
        onClose={() => setShowPreview(false)}
        consultationDate={formatDateTime(consultation.scheduled_time)}
        doctorNote={doctorNote}
        planNextSteps={planNextSteps}
        recommendations={recommendations}
        doctorName={consultation.doctor_name || authUser?.displayName || ""}
        signatureUrl={consultation.signature_url}
      />
    </div>
  );
}
